import { generateDenominatorNumerator } from '~/bootcamp/Activities/Fractions/Fractions.utils'
import { getFactors, randomIntFromInterval, randomItemFromList } from '~/bootcamp/Activities/utils/activities.utils'

import { getRangesByDifficulty } from './constants'

export interface QuestionValue {
  num: number
  denom: number
}

/**
 * Responsible to generate final term after adjustment
 * @param difficultyLevel
 * @param taskHistory
 * @param taskHistory callBackCount
 * @returns { numerator: number; denominator: number;  }
 */
export function generateTerms(
  difficultyLevel: number,
  taskHistory: number[],
  callBackCount = 0,
): { numerator: number; denominator: number; ans: QuestionValue } {
  const { minNumerator, maxNumerator, minDenominator, maxDenominator } = getRangesByDifficulty(difficultyLevel)
  let { numerator, denominator } = generateDenominatorNumerator(
    minNumerator,
    maxNumerator,
    minDenominator,
    maxDenominator,
    taskHistory,
  )
  // after getting the value of numerator and denominator, try to adjust value to get more factors.
  const numFactors: number[] = getFactors(numerator, [1])
  const denomFactors: number[] = getFactors(denominator, [1])

  if (denomFactors.length !== 0) {
    numerator = randomItemFromList(denomFactors) as number
  } else if (numFactors.length !== 0) {
    const min = Math.ceil(minDenominator / numerator)
    const max = Math.floor(maxDenominator / numerator)
    const randomVal = randomIntFromInterval(min, max)
    denominator = numerator * randomVal
  }

  // get common factors of numerator and denominator and check the common factors are available in history or not
  const commonFactors = getCommonFactors(numerator, denominator)
  if (
    (commonFactors.length === 0 || numerator === denominator || taskHistory.includes(numerator)) &&
    callBackCount < 10
  ) {
    return generateTerms(difficultyLevel, taskHistory, callBackCount + 1)
  }

  const ans = {
    num: denominator % numerator === 0 ? 1 : numerator,
    denom: denominator % numerator === 0 ? denominator / numerator : denominator,
  }

  return { numerator, denominator, ans }
}

/**
 * Responsible to generate common factors
 * @param numerator
 * @param denominator
 * @returns list of common factors
 */
export function getCommonFactors(numerator: number, denominator: number): number[] {
  const numFactors: number[] = getFactors(numerator, [1])
  const denomFactors: number[] = getFactors(denominator, [1])
  // filter common factors
  const commonFactors = numFactors.filter((value) => denomFactors.includes(value))

  return commonFactors
}
